import { atom, useStore } from "jotai";
import type { createStore } from "jotai";
import { atomWithStorage, RESET } from "jotai/utils";
import React from "react";
import { omit } from "remeda";
import { safeJSONStorage } from "utils/safeStorage";
import { chatLocationOf, conversationIdAtom, pageConversationsAtom, setConversationPointerAtom } from "../lib/storage";
import type { QwickUIMessage } from "../lib/types";

// Persisted for a reload mid-takeover; not synced across tabs, so another tab's takeover cannot hide this dock.
export const dockedConversationAtom = atomWithStorage<{ conversationId: string | null } | null>(
  "qwick-ai-chat-docked-conversation",
  null,
  omit(safeJSONStorage<{ conversationId: string | null } | null>(), ["subscribe"]),
  {
    getOnInit: true,
  },
);

/** The dock's transcript, handed to the takeover it opens: the server may not have stored the turn that opened it yet. */
export const dockMessagesAtom = atom<Array<QwickUIMessage> | null>(null);

/** Whether the docked chat has a turn in flight (or a page-tool step to continue): it stays mounted until it is done. */
export const dockBusyAtom = atom(false);

// The location the saved pointer belongs to. Not persisted: after a reload the hand-back falls back to the app's location.
const dockedLocationAtom = atom<number | undefined>(undefined);

/**
 * Drops the docked chat's saved pointer. Onboarding calls it instead of handing the pointer back when it opens a canvas
 * or leaves a draft: the server seeds and reopens a draft only in the conversation that recorded it.
 */
export const keepOwnConversation = (store: ReturnType<typeof createStore>) => {
  store.set(dockedConversationAtom, RESET);
  store.set(dockedLocationAtom, undefined);
};

/** Puts the docked chat's saved pointer back; a no-op once it has been, so either the takeover or the dock may call it. */
export const handBackDockedConversation = (store: ReturnType<typeof createStore>) => {
  const saved = store.get(dockedConversationAtom);
  if (!saved) return;
  // The takeover can end after a header switch, so the pointer goes back to the location it was saved from.
  store.set(setConversationPointerAtom, {
    locationId: store.get(dockedLocationAtom) ?? chatLocationOf(store),
    conversationId: saved.conversationId,
  });
  keepOwnConversation(store);
};

/** Saves the docked chat's pointer and the location it belongs to, unless a takeover already has. */
export const saveDockedConversation = (store: ReturnType<typeof createStore>) => {
  if (store.get(dockedConversationAtom)) return;
  store.set(dockedConversationAtom, { conversationId: store.get(conversationIdAtom) });
  store.set(dockedLocationAtom, chatLocationOf(store));
};

/**
 * The takeover's own conversation, carried on after a reload mid-takeover (null starts one); the dock's comes back.
 * `pageKey` (`"<user>:<location>"`, the posting page only): the page's conversation is kept under it while the takeover runs,
 * and a later opening of the page carries it on.
 */
export const useOwnConversation = (pageKey?: string) => {
  const store = useStore();
  const [resumeId] = React.useState(() => {
    const saved = store.get(dockedConversationAtom);
    const current = store.get(conversationIdAtom);
    if (saved && current !== null && current !== saved.conversationId) return current;
    return pageKey ? (store.get(pageConversationsAtom)[pageKey] ?? null) : null;
  });
  // A layout effect sets the pointer before any passive effect below it can send the first turn.
  React.useLayoutEffect(() => {
    saveDockedConversation(store);
    store.set(conversationIdAtom, resumeId);
    // Only what this takeover's chat names: the subscription ends before the dock's pointer is handed back.
    const unsubscribe = pageKey
      ? store.sub(conversationIdAtom, () => {
          const id = store.get(conversationIdAtom);
          if (id) store.set(pageConversationsAtom, (all) => (all[pageKey] === id ? all : { ...all, [pageKey]: id }));
        })
      : undefined;
    return () => {
      unsubscribe?.();
      handBackDockedConversation(store);
    };
  }, [store, resumeId, pageKey]);
  return resumeId;
};
